import { CSSProperties, ChangeEvent, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './TextInput.module.css';
import { SvgIcon } from '../SVGIcon';
import { SVGIconType } from '../../../constants/svgIcon';

export type TextInputProps = {
  value: string;
  placeholder?: string;
  isClicked?: boolean;
  disabled?: boolean;
  type?: 'password' | 'text';
  errorMessage?: string;
  maxLength?: number;
  style?: CSSProperties | undefined;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const TextInput = memo(
  ({
    value,
    placeholder,
    isClicked,
    type = 'text',
    disabled,
    errorMessage,
    maxLength,
    style,
    onChange,
    onFocus,
    onBlur,
  }: TextInputProps) => {
    const hasError =
      (isClicked && !value) ||
      errorMessage ||
      (maxLength && value.length > maxLength);
    const { t } = useTranslation();

    const [showPassword, setShowPassword] = useState(false);

    const toggleShowPassword = () => {
      setShowPassword(!showPassword);
    };

    return (
      <div className={styles.textInput}>
        <div className={styles.inputWrapper}>
          <input
            type={showPassword ? 'text' : type}
            className={`${styles.customInput} ${
              hasError ? styles.customInputError : ''
            }`}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            style={{
              backgroundColor: disabled ? '#E5E5E5' : undefined,
              ...style,
            }}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          {type === 'password' && (
            <button
              type="button"
              className={styles.showPasswordButton}
              onClick={toggleShowPassword}
            >
              {showPassword ? (
                <SvgIcon
                  name={SVGIconType.PreviewHide}
                  width={24}
                  height={24}
                  stroke="#333333"
                />
              ) : (
                <SvgIcon
                  name={SVGIconType.PreviewShow}
                  width={24}
                  height={24}
                  stroke="#333333"
                />
              )}
            </button>
          )}
        </div>
        {errorMessage && maxLength ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{t(errorMessage)}</p>
            <p
              className={
                value.length > maxLength
                  ? styles.errorTextLength
                  : styles.textLength
              }
            >{`${value.length} / ${maxLength}`}</p>
          </div>
        ) : maxLength ? (
          <p
            className={
              value.length > maxLength
                ? styles.errorTextLength
                : styles.textLength
            }
          >{`${value.length} / ${maxLength}`}</p>
        ) : errorMessage ? (
          <p className={styles.errorMessage}>{t(errorMessage)}</p>
        ) : null}
      </div>
    );
  },
);
